// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Web Mercator maths for the hole view. Kept free of React and of react-native so the
// framing rules — "cover tee to green, plus a quarter of the width on either side" —
// can be asserted directly rather than through a rendered tree.

/** WGS84 equatorial circumference in metres; one unit of world fraction at the equator. */
const EARTH_CIRCUMFERENCE_METERS = 40075016.686;

export const TILE_SIZE = 256;

/** Above this the imagery providers start returning blank tiles for most of the world. */
export const MAX_TILE_ZOOM = 19;

/**
 * Smallest span the view will frame, in metres. A hole with a single known point — or a
 * tee sitting on top of its green because the estimates have not separated yet — would
 * otherwise ask for infinite zoom.
 */
const MIN_SPAN_METERS = 60;

export type LatLon = { lat: number; lon: number };

export type TilePlacement = {
  x: number;
  y: number;
  z: number;
  /** Offset of the tile's top-left corner inside the viewport, in pixels. */
  left: number;
  top: number;
};

export type Point = { x: number; y: number };

/**
 * Rotates a point about an origin, in screen coordinates.
 *
 * Screen y grows downwards, so a positive angle reads as clockwise — the same direction CSS
 * and React Native's `rotate` transform turn, which is what lets the same angle drive both
 * the maths and the container.
 */
export const rotatePoint = (point: Point, origin: Point, degrees: number): Point => {
  if (!degrees) return point;
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  return {
    x: origin.x + dx * cos - dy * sin,
    y: origin.y + dx * sin + dy * cos,
  };
};

export type HoleViewLayout = {
  /** Tile pyramid level the imagery is fetched from. */
  zoom: number;
  /** World width in pixels at the rendered scale. Not necessarily 256 · 2^zoom. */
  scale: number;
  /** Size each tile is drawn at, which is how fractional zoom is expressed. */
  tileSize: number;
  width: number;
  height: number;
  /** World-pixel coordinate of the viewport's top-left corner at `scale`. */
  originX: number;
  originY: number;
  tiles: TilePlacement[];
  /**
   * Degrees the whole mosaic is turned by so the hole plays bottom to top.
   *
   * Zero for a north-up view. When it is not zero, `width`/`height` describe the square the
   * mosaic is built for — larger than the visible canvas, because a rotated square has to
   * be big enough to cover the corners — and `viewportWidth`/`viewportHeight` describe what
   * the player actually sees.
   */
  rotationDegrees: number;
  viewportWidth: number;
  viewportHeight: number;
};

export type HoleViewInput = {
  tees: LatLon[];
  green: LatLon | null;
  /** Viewport size in pixels. */
  width: number;
  height: number;
  /** Extra share of the framed east–west span added on each side. Defaults to 0.25. */
  horizontalPadFraction?: number;
  /** Extra share of the framed north–south span added on each side. Defaults to 0.1. */
  verticalPadFraction?: number;
  /**
   * Further points the frame must contain, such as the player's own position. Passing one
   * that is nowhere near the hole would zoom the view out to nothing, so the caller decides
   * what counts as near — see {@link distanceToSegmentMeters}.
   */
  extraPoints?: LatLon[];
  /**
   * Compass bearing the hole plays along, tee to green. Supplied, the whole view is turned
   * so the hole runs bottom to top — the way every golf app draws it. Omitted, the view is
   * north-up.
   */
  holeBearing?: number | null;
  maxZoom?: number;
  /**
   * Overrides {@link MIN_SPAN_METERS}. A hole with nothing confirmed but a single point
   * would otherwise ask for infinite zoom, which the floor exists to stop — but a caller
   * fitting to a frame it already computed precisely (matching another view's own fit,
   * padding included) needs that floor *out of the way*, not applied a second time on top
   * of padding that already accounted for it. Pass 0 in that case.
   */
  minSpanMeters?: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const isFinitePoint = (point: LatLon | null | undefined): point is LatLon =>
  !!point &&
  Number.isFinite(point.lat) &&
  Number.isFinite(point.lon) &&
  Math.abs(point.lat) <= 90 &&
  Math.abs(point.lon) <= 180;

/** Longitude to a 0..1 fraction of the world, west to east. */
export const lonToWorldFraction = (lon: number): number => (lon + 180) / 360;

/** Latitude to a 0..1 fraction of the world, north to south (Web Mercator). */
export const latToWorldFraction = (lat: number): number => {
  // Mercator is undefined at the poles; the clamp is the usual ±85.05° cutoff expressed
  // in sine form.
  const siny = clamp(Math.sin((lat * Math.PI) / 180), -0.9999, 0.9999);
  return 0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI);
};

/**
 * Metres expressed as a fraction of the world at a given latitude.
 *
 * Mercator is conformal, so the same factor applies to both axes.
 */
export const metersToWorldFraction = (meters: number, lat: number): number => {
  const groundCircumference = EARTH_CIRCUMFERENCE_METERS * Math.cos((lat * Math.PI) / 180);
  if (!Number.isFinite(groundCircumference) || groundCircumference <= 0) return 0;
  return meters / groundCircumference;
};

/**
 * Two opposite corners of a square of ground centred on a point.
 *
 * For framing a view that has nothing in it yet. A hole with no tee and no green marked has
 * no coordinates to fit, so the editor frames a patch of the course around the club's own
 * position instead — enough ground to find the hole on and start clicking.
 *
 * Returned as points rather than as a layout so it feeds the ordinary framing path: the same
 * fit, the same padding, the same rules as a hole that does have geometry.
 */
export const framingBoxAround = (center: LatLon, spanMeters: number): LatLon[] => {
  if (!isFinitePoint(center) || !(spanMeters > 0)) return [];

  const halfLat = (spanMeters / 2 / EARTH_CIRCUMFERENCE_METERS) * 360;
  const cosLat = Math.cos((center.lat * Math.PI) / 180);
  // Degrees of longitude shrink towards the poles; without this the box is a tall rectangle
  // at Swedish latitudes rather than the square patch of ground it is meant to be.
  const halfLon = Math.abs(cosLat) < 1e-6 ? halfLat : halfLat / cosLat;

  return [
    { lat: center.lat - halfLat, lon: center.lon - halfLon },
    { lat: center.lat + halfLat, lon: center.lon + halfLon },
  ];
};

/** Great-circle distance in metres. Used for the tee-to-green readout. */
export const distanceMeters = (from: LatLon, to: LatLon): number => {
  const radius = 6371008.8;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lon - from.lon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * radius * Math.asin(Math.min(1, Math.sqrt(a)));
};

/**
 * Where a point falls on a segment, and how far off it lies.
 *
 * `t` is the fraction along `from`→`to` of the nearest point on the segment, clamped to the
 * ends — 0 at or behind `from`, 1 at or beyond `to`. `distanceMeters` is the distance to
 * that nearest point.
 *
 * Flat-earth over a few hundred metres. The error is far below the GPS accuracy it is
 * compared against.
 */
export const projectOntoSegment = (
  point: LatLon,
  from: LatLon,
  to: LatLon
): { t: number; distanceMeters: number } => {
  const metresPerDegreeLat = 111132.95;
  const metresPerDegreeLon = metresPerDegreeLat * Math.cos((from.lat * Math.PI) / 180);
  const toLocal = (p: LatLon) => ({
    x: (p.lon - from.lon) * metresPerDegreeLon,
    y: (p.lat - from.lat) * metresPerDegreeLat,
  });

  const p = toLocal(point);
  const b = toLocal(to);
  const lengthSquared = b.x * b.x + b.y * b.y;
  // A zero-length segment is just a point, which is what the endpoint clamp below reduces to.
  const t = lengthSquared > 0 ? clamp((p.x * b.x + p.y * b.y) / lengthSquared, 0, 1) : 0;
  return { t, distanceMeters: Math.hypot(p.x - b.x * t, p.y - b.y * t) };
};

/**
 * Shortest distance in metres from a point to the tee-to-green line, endpoints included.
 *
 * This is the "am I on this hole" measure: it is small anywhere along the corridor and grows
 * as soon as you walk off either end, which a distance to the green alone does not do — that
 * one is equally small standing on the green and standing on the next tee behind it.
 */
export const distanceToSegmentMeters = (point: LatLon, from: LatLon, to: LatLon): number =>
  projectOntoSegment(point, from, to).distanceMeters;

/**
 * Shortest distance in metres from a point to a hazard's boundary — how far there is to walk
 * (or carry) before first touching it, not the distance to its centre or its far side.
 *
 * The minimum over every edge, wrapping the ring closed even when it was not stored that way.
 * A point already inside the ring still returns a small positive number rather than zero or a
 * negative "depth" — for a bunker or a pond that is the only reading anyone plans a shot by.
 */
export const distanceToRingMeters = (point: LatLon, ring: LatLon[]): number => {
  let nearest = Infinity;
  for (let index = 0; index < ring.length; index += 1) {
    const from = ring[index];
    const to = ring[(index + 1) % ring.length];
    nearest = Math.min(nearest, distanceToSegmentMeters(point, from, to));
  }
  return nearest;
};

/**
 * A point just clear of a ring's own bounding box, in whatever space the ring's points are
 * already in — screen pixels, once a caller has projected a boundary through a layout.
 *
 * Anchored at the box's right edge, vertically centred, so a label placed here reads beside
 * the shape rather than over it: pixel space is worked in deliberately, not the underlying
 * lat/lon, so "right" means right on screen even once the view has been turned to run a hole
 * bottom to top. Null only when there are no points to anchor against.
 */
export const pointBesideRing = (points: Point[], gap: number): Point | null => {
  if (points.length === 0) return null;
  const maxX = Math.max(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxY = Math.max(...points.map((point) => point.y));
  return { x: maxX + gap, y: (minY + maxY) / 2 };
};

/**
 * Whether a point lies inside a closed ring, by the standard ray-casting count of how many
 * times a ray from the point crosses the ring's edges — odd is inside, even is outside.
 *
 * Coordinate-system agnostic by design: the same test serves a tap on the satellite view
 * (ring in lon/lat degrees) and a tap on the schematic drawing (ring in the guide's own
 * local metres plane), because it never looks at what x and y mean, only their arithmetic.
 */
export const pointInPolygon = (point: Point, ring: Point[]): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const a = ring[i];
    const b = ring[j];
    const crosses = a.y > point.y !== b.y > point.y;
    if (!crosses) continue;
    const xAtPointY = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (point.x < xAtPointY) inside = !inside;
  }
  return inside;
};

/**
 * Frames a hole inside a viewport and lists the tiles that cover it.
 *
 * The bounding box spans every tee and the green. It is then widened by
 * `horizontalPadFraction` of its own east–west span on each side, so a hole that runs
 * across the screen shows a quarter of its length of surrounding ground left and right.
 * The vertical padding is deliberately smaller: it exists so the tee and green markers
 * are not half-clipped by the frame, not to show more ground.
 *
 * Zoom is the largest integer level at which the padded box still fits both axes, so the
 * hole is never cropped — the slack lands on whichever axis is less demanding.
 *
 * Returns null when there is nothing to frame or the viewport has not been measured yet.
 */
export const computeHoleViewLayout = ({
  tees,
  green,
  width,
  height,
  horizontalPadFraction = 0.25,
  verticalPadFraction = 0.1,
  extraPoints = [],
  holeBearing = null,
  maxZoom = MAX_TILE_ZOOM,
  minSpanMeters = MIN_SPAN_METERS,
}: HoleViewInput): HoleViewLayout | null => {
  if (!(width > 0) || !(height > 0)) return null;

  const points = [
    ...tees.filter(isFinitePoint),
    ...(isFinitePoint(green) ? [green] : []),
    ...extraPoints.filter(isFinitePoint),
  ];
  if (points.length === 0) return null;

  // Turning the content by minus the hole's bearing points the tee-to-green line straight
  // up. Everything below is then measured in that turned space, so the fit describes what
  // the player will actually see rather than the north-up box it came from.
  const rotationDegrees =
    holeBearing === null || !Number.isFinite(holeBearing) ? 0 : -normalizeBearing(holeBearing);

  const projected = points.map((point) => ({
    x: lonToWorldFraction(point.lon),
    y: latToWorldFraction(point.lat),
  }));
  const pivot = {
    x: projected.reduce((sum, p) => sum + p.x, 0) / projected.length,
    y: projected.reduce((sum, p) => sum + p.y, 0) / projected.length,
  };
  const turned = projected.map((p) => rotatePoint(p, pivot, rotationDegrees));

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const point of turned) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }

  const centerLat = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const minSpan = metersToWorldFraction(minSpanMeters, centerLat);

  // Turned, the hole is a vertical line with no width of its own, so "a quarter of the
  // width either side" has nothing to take a quarter of. The padding is measured against
  // the hole's own length instead, which is the corridor a golfer wants to see.
  const holeLength = maxY - minY;
  const crossSpan = rotationDegrees === 0 ? maxX - minX : Math.max(maxX - minX, holeLength);
  const padX = crossSpan * horizontalPadFraction;
  const padY = holeLength * verticalPadFraction;
  let spanX = Math.max(maxX - minX + padX * 2, minSpan);
  let spanY = Math.max(holeLength + padY * 2, minSpan);
  // Guard against a degenerate span surviving the max above (a zero-cosine latitude).
  if (!(spanX > 0)) spanX = 1 / 360;
  if (!(spanY > 0)) spanY = 1 / 360;

  // The centre of the framed box, carried back out of the turned space the fit was measured
  // in so the mosaic can be built around it in ordinary world coordinates.
  const turnedCenter = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  const { x: centerX, y: centerY } = rotatePoint(turnedCenter, pivot, -rotationDegrees);

  // The scale that exactly fits the padded box. Honouring it — rather than snapping down
  // to a whole tile zoom — is what keeps the margins the framing rule asks for; a snapped
  // zoom would leave up to twice the requested slack.
  const scale = Math.min(width / spanX, height / spanY);

  // A turned square has to reach the corners of the canvas, so the mosaic is built for the
  // diagonal. Square, because the same box has to cover every rotation.
  const box = rotationDegrees === 0 ? null : Math.ceil(Math.hypot(width, height));

  return layoutAt({
    scale,
    centerX,
    centerY,
    width: box ?? width,
    height: box ?? height,
    maxZoom,
    rotationDegrees,
    viewportWidth: width,
    viewportHeight: height,
  });
};

/** Local copy so the geometry here owes nothing to the wind module, which depends on it. */
const normalizeBearing = (degrees: number): number => ((degrees % 360) + 360) % 360;

export type CameraInput = {
  /** The coordinate to hold at the centre of the viewport. */
  center: LatLon;
  /** Ground distance the viewport's width covers, in metres. */
  spanMeters: number;
  /**
   * Compass bearing the view plays along. Supplied, the view is turned so that direction
   * runs bottom to top, exactly as {@link HoleViewInput.holeBearing} does. Omitted, north-up.
   */
  bearingDegrees?: number | null;
  /** Viewport size in pixels. */
  width: number;
  height: number;
  maxZoom?: number;
};

/**
 * The layout for an explicit camera — a centre, a ground span and a bearing — rather than
 * for a fit around known points.
 *
 * {@link computeHoleViewLayout} answers "show me this hole"; this answers "put the camera
 * *here*". It exists for animation: a flyover glides the centre along the hole while the
 * span and bearing stay put, and each frame is built by the same rules as the static fit
 * because both funnel into the same private layout builder.
 */
export const computeLayoutAt = ({
  center,
  spanMeters,
  bearingDegrees = null,
  width,
  height,
  maxZoom = MAX_TILE_ZOOM,
}: CameraInput): HoleViewLayout | null => {
  if (!(width > 0) || !(height > 0)) return null;
  if (!isFinitePoint(center) || !(spanMeters > 0)) return null;

  const rotationDegrees =
    bearingDegrees === null || !Number.isFinite(bearingDegrees)
      ? 0
      : -normalizeBearing(bearingDegrees);

  const spanFraction = metersToWorldFraction(spanMeters, center.lat);
  if (!(spanFraction > 0)) return null;
  const scale = width / spanFraction;

  // Same rule as the fit: a turned mosaic is built for the diagonal square so it still
  // covers the canvas corners at any rotation.
  const box = rotationDegrees === 0 ? null : Math.ceil(Math.hypot(width, height));

  return layoutAt({
    scale,
    centerX: lonToWorldFraction(center.lon),
    centerY: latToWorldFraction(center.lat),
    width: box ?? width,
    height: box ?? height,
    maxZoom,
    rotationDegrees,
    viewportWidth: width,
    viewportHeight: height,
  });
};

/**
 * The layout for a given scale and centre, with the tiles that cover it.
 *
 * Shared by the initial fit and by {@link applyViewTransform}, so a zoomed view is built by
 * exactly the same rules as the one it started from.
 */
const layoutAt = ({
  scale,
  centerX,
  centerY,
  width,
  height,
  maxZoom,
  rotationDegrees = 0,
  viewportWidth = width,
  viewportHeight = height,
}: {
  scale: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  maxZoom: number;
  rotationDegrees?: number;
  viewportWidth?: number;
  viewportHeight?: number;
}): HoleViewLayout => {
  // Imagery comes from a whole pyramid level. Rounding rather than flooring keeps the
  // resampling factor within √2 either way, so tiles stay sharp without the tile count
  // exploding.
  const zoom = clamp(Math.round(Math.log2(scale / TILE_SIZE)), 0, maxZoom);
  const tileCount = 2 ** zoom;
  const tileSize = scale / tileCount;

  const originX = centerX * scale - width / 2;
  const originY = centerY * scale - height / 2;

  const firstTileX = Math.floor(originX / tileSize);
  const lastTileX = Math.floor((originX + width) / tileSize);
  const firstTileY = Math.floor(originY / tileSize);
  const lastTileY = Math.floor((originY + height) / tileSize);

  const tiles: TilePlacement[] = [];
  for (let ty = firstTileY; ty <= lastTileY; ty += 1) {
    // Rows outside the world have no imagery; columns wrap instead.
    if (ty < 0 || ty >= tileCount) continue;
    for (let tx = firstTileX; tx <= lastTileX; tx += 1) {
      const wrappedX = ((tx % tileCount) + tileCount) % tileCount;
      tiles.push({
        x: wrappedX,
        y: ty,
        z: zoom,
        left: tx * tileSize - originX,
        top: ty * tileSize - originY,
      });
    }
  }

  return {
    zoom,
    scale,
    tileSize,
    width,
    height,
    originX,
    originY,
    tiles,
    rotationDegrees,
    viewportWidth,
    viewportHeight,
  };
};

/** Where a coordinate lands inside the laid-out viewport, in pixels. */
export const projectToLayout = (point: LatLon, layout: HoleViewLayout): { x: number; y: number } => ({
  x: lonToWorldFraction(point.lon) * layout.scale - layout.originX,
  y: latToWorldFraction(point.lat) * layout.scale - layout.originY,
});

/** How far in the player may zoom past the framing that fits the hole. */
export const MAX_VIEW_ZOOM = 8;
export const MIN_VIEW_ZOOM = 1;

/**
 * How far the editor may zoom out past the framing that fits the hole.
 *
 * The player's view has no reason to go below the fit — the frame is built from the hole and
 * the hole is the subject. Editing is the opposite case: the frame is built from geometry
 * that may be wrong, so the ground you need is outside it, and a quarter scale shows about
 * four times the width in each direction, which reaches the neighbouring holes.
 */
export const MIN_EDIT_ZOOM = 0.25;

/**
 * How far a pan may travel when the caller allows free panning, in viewports.
 *
 * Bounded rather than unlimited so a stray drag cannot strand the map in empty sea with no
 * way back but the fit button; four viewports is far enough to walk to a neighbouring hole,
 * and anything further is a job for the coordinate field.
 */
const FREE_PAN_VIEWPORTS = 4;

export type ViewTransform = {
  /** Multiplier on the fitted scale. 1 is the framing that shows the whole hole. */
  zoom: number;
  /** Viewport pixels the content is shifted by, after zooming. */
  panX: number;
  panY: number;
};

export const IDENTITY_VIEW: ViewTransform = { zoom: 1, panX: 0, panY: 0 };

/**
 * Zooms and pans a fitted layout.
 *
 * Zoom is about the centre of the viewport, so the hole does not slide away under a pinch,
 * and the pan is applied afterwards in screen pixels — which is what makes a drag track the
 * finger one-to-one whatever the zoom happens to be.
 *
 * Pan is clamped so the framed hole can never be pushed entirely off screen: at rest the
 * limit is zero, and it grows with the zoom, which is exactly the room zooming created.
 */
export const applyViewTransform = (
  layout: HoleViewLayout,
  { zoom, panX, panY }: ViewTransform,
  maxZoom: number = MAX_TILE_ZOOM,
  { minZoom = MIN_VIEW_ZOOM, freePan = false }: { minZoom?: number; freePan?: boolean } = {}
): HoleViewLayout => {
  const clampedZoom = clamp(zoom, minZoom, MAX_VIEW_ZOOM);
  if (clampedZoom === 1 && panX === 0 && panY === 0) return layout;

  const scale = layout.scale * clampedZoom;
  // The world point currently at the centre of the viewport stays there.
  const centerX = (layout.originX + layout.width / 2) / layout.scale;
  const centerY = (layout.originY + layout.height / 2) / layout.scale;

  /**
   * How far the pan may travel.
   *
   * Zoomed in, it is the hidden overflow: exactly the content the viewport is cropping, so a
   * drag can reach every part of the frame and no further. That formula goes to zero at the
   * fit and negative below it — which silently clamped every pan to nothing, so a view that
   * had been given permission to pan still would not move. Editing needs to leave the frame
   * altogether, because the frame is built from the geometry being corrected, so it is given
   * a fixed allowance instead of one derived from a zoom it has gone past.
   */
  const overflowX = (layout.viewportWidth * (clampedZoom - 1)) / 2;
  const overflowY = (layout.viewportHeight * (clampedZoom - 1)) / 2;
  const slackX = freePan
    ? Math.max(overflowX, layout.viewportWidth * FREE_PAN_VIEWPORTS)
    : Math.max(0, overflowX);
  const slackY = freePan
    ? Math.max(overflowY, layout.viewportHeight * FREE_PAN_VIEWPORTS)
    : Math.max(0, overflowY);
  // The pan is measured on screen; the mosaic underneath it is turned, so the shift has to
  // be turned with it or a drag would slide the map off at an angle.
  const onScreen = {
    x: clamp(panX, -slackX, slackX),
    y: clamp(panY, -slackY, slackY),
  };
  const { x: shiftX, y: shiftY } = rotatePoint(
    onScreen,
    { x: 0, y: 0 },
    -layout.rotationDegrees
  );

  const zoomed = layoutAt({
    scale,
    centerX,
    centerY,
    width: layout.width,
    height: layout.height,
    maxZoom,
    rotationDegrees: layout.rotationDegrees,
    viewportWidth: layout.viewportWidth,
    viewportHeight: layout.viewportHeight,
  });

  // Shifting the origin the opposite way moves the imagery with the finger.
  return retileAtOrigin(zoomed, zoomed.originX - shiftX, zoomed.originY - shiftY);
};

/** Re-lists the tiles for a layout whose origin has moved, leaving its scale alone. */
const retileAtOrigin = (
  layout: HoleViewLayout,
  originX: number,
  originY: number
): HoleViewLayout => {
  const centerX = (originX + layout.width / 2) / layout.scale;
  const centerY = (originY + layout.height / 2) / layout.scale;
  return layoutAt({
    scale: layout.scale,
    centerX,
    centerY,
    width: layout.width,
    height: layout.height,
    maxZoom: layout.zoom,
    rotationDegrees: layout.rotationDegrees,
    viewportWidth: layout.viewportWidth,
    viewportHeight: layout.viewportHeight,
  });
};

/**
 * Where a point on the visible canvas lands inside the turned mosaic.
 *
 * The mosaic is a larger square, turned about its own centre and centred on the canvas, so
 * a press has to be walked back through both of those before it means anything. Getting the
 * sign wrong here mirrors every tap without looking wrong on screen, which is why it is a
 * named function with a round-trip test rather than arithmetic inline in a handler.
 */
export const viewportToLayoutPoint = (point: Point, layout: HoleViewLayout): Point => {
  const fromCanvasCentre = {
    x: point.x - layout.viewportWidth / 2,
    y: point.y - layout.viewportHeight / 2,
  };
  const unturned = rotatePoint(fromCanvasCentre, { x: 0, y: 0 }, -layout.rotationDegrees);
  return { x: unturned.x + layout.width / 2, y: unturned.y + layout.height / 2 };
};

/** The inverse of {@link viewportToLayoutPoint}: mosaic coordinates back onto the canvas. */
export const layoutToViewportPoint = (point: Point, layout: HoleViewLayout): Point => {
  const fromBoxCentre = { x: point.x - layout.width / 2, y: point.y - layout.height / 2 };
  const turned = rotatePoint(fromBoxCentre, { x: 0, y: 0 }, layout.rotationDegrees);
  return {
    x: turned.x + layout.viewportWidth / 2,
    y: turned.y + layout.viewportHeight / 2,
  };
};

/**
 * The coordinate under a point in the viewport — the inverse of {@link projectToLayout}.
 *
 * This is what turns a tap into a place on the course, so it has to invert the projection
 * exactly rather than approximately: a target dropped at the finger must not sit a few
 * metres off, because a distance is quoted from it.
 */
export const unprojectFromLayout = (
  point: { x: number; y: number },
  layout: HoleViewLayout
): LatLon => {
  const fx = (point.x + layout.originX) / layout.scale;
  const fy = (point.y + layout.originY) / layout.scale;
  // Mirrors latToWorldFraction: fy = 0.5 - artanh(sin lat) / 2π.
  const lat = (Math.asin(Math.tanh(Math.PI * (1 - 2 * fy))) * 180) / Math.PI;
  return { lat, lon: fx * 360 - 180 };
};

/** Fills `{z}`/`{x}`/`{y}` in a raster tile template. */
export const buildTileUrl = (template: string, tile: TilePlacement): string =>
  template
    .replace(/\{z\}/g, String(tile.z))
    .replace(/\{x\}/g, String(tile.x))
    .replace(/\{y\}/g, String(tile.y));
